import {colors, radii, tokens} from '@commercial-helios/ui/theme'
import * as React from 'react'
import styled, {css} from 'styled-components'

import type {RefType as CarouselRefType} from '@/components/carousel'
import Carousel from '@/components/carousel'
import {ArrowLeft} from '@helios/shared/images/icons/arrow-left'
import {ArrowRight} from '@helios/shared/images/icons/arrow-right'
import {gutters, Container} from '@helios/shared/styles/grid'
import {focusStyles} from '@helios/shared/styles/utils'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'
import mq from '@helios/shared/utils/media-query'

/*
 * @desc: This component needs to be able to expand to the full width of the page.
 * Otherwise the alignment of the cards will be off
 */
export const CardsCarousel = ({children}: {children: React.ReactNode}) => {
    const carouselRef = React.useRef<CarouselRefType>(null)
    const [visibleCards, setVisibleCards] = React.useState<number[]>([])

    const onGoToClick = React.useCallback((index: number) => {
        track(TrackingEvent.SliderCardsButtonActioned, {action: 'clicked'})

        carouselRef.current?.slideGoTo(index)
    }, [])

    const handleSlideChange = (index: number, inView: boolean) =>
        setVisibleCards((prev) =>
            inView
                ? Array.from(new Set([...prev, index].sort((a, b) => a - b)))
                : [...prev].filter((el) => el !== index)
        )

    return (
        <>
            <StyledCarousel ref={carouselRef} onSlideChange={handleSlideChange} threshold={0}>
                {children}
            </StyledCarousel>

            <ButtonsWrapper>
                <NavigationButton onClick={() => onGoToClick(visibleCards[0])} aria-label="prev">
                    <StyledArrowLeft />
                </NavigationButton>

                <NavigationButton
                    onClick={() => onGoToClick(visibleCards[visibleCards.length - 1] || 0)}
                    aria-label="next"
                >
                    <StyledArrowRight />
                </NavigationButton>
            </ButtonsWrapper>
        </>
    )
}

const MOBILE_BREAKPOINT = 'xs'

const ButtonsWrapper = styled(Container)`
    display: grid;
    grid-auto-flow: column;
    column-gap: ${tokens.spacing40};
    justify-content: center;
    margin-top: ${tokens.spacing20};

    ${mq.min(MOBILE_BREAKPOINT)} {
        justify-content: start;
        margin-top: ${tokens.spacing40};
    }
`

const NavigationButton = styled.button`
    display: grid;
    width: 48px;
    height: 48px;
    color: ${colors.border.secondary};
    cursor: pointer;
    background-color: transparent;
    border: 1px solid ${colors.border.secondary};
    border-radius: ${radii.circle};
    place-items: center;

    :focus {
        ${focusStyles()}
        border: 1px solid transparent;
    }

    ${mq.min(MOBILE_BREAKPOINT)} {
        width: 72px;
        height: 72px;
    }
`

const arrowsStyles = css`
    width: 60%;

    ${mq.max(MOBILE_BREAKPOINT)} {
        /* iOS Safari doesn't handle percentage widths correctly */
        @supports (-webkit-backdrop-filter: blur(1px)) {
            width: 150%;
        }
    }
`

const StyledArrowLeft = styled(ArrowLeft)`
    ${arrowsStyles}
`
const StyledArrowRight = styled(ArrowRight)`
    ${arrowsStyles}
`

const StyledCarousel = styled(Carousel)`
    display: grid;
    grid-auto-columns: 92%;
    ${gutters};
    column-gap: ${tokens.spacing20};
    scroll-snap-type: unset;

    ${mq.min(MOBILE_BREAKPOINT)} {
        column-gap: ${tokens.spacing40};
        grid-auto-columns: 44%;
    }

    ${mq.min('md')} {
        grid-auto-columns: 386px;
    }

    ${mq.min('xl')} {
        padding-left: calc((100vw - 1440px + 200px) / 2);
        padding-right: calc((100vw - 1440px + 200px) / 2);
    }
`
